// Orthographic globe maths shared by the camera (drag, picking) and the globe renderer.
//
// The globe is viewed with north up. Its screen frame is the sphere's own frame, centred on the
// point (lon0, lat0) that faces the viewer:
//
//        y (north)
//        │   ● p = (x, y, z) on the unit sphere
//        │  ╱
//        └──── x (east)         z points at the viewer
//       ╱
//
// All angles are radians.

/** A place on the sphere. */
export interface Spot {
  lon: number;
  lat: number;
}

/** A point on the unit sphere in the screen frame. */
export interface ScreenPoint {
  x: number;
  y: number;
  z: number;
}

const TAU = Math.PI * 2;
const POLE_EPS = 1e-6;

/** Wrap an angle into (-π, π]. */
export function wrapPi(a: number): number {
  const w = a % TAU;
  if (w > Math.PI) {
    return w - TAU;
  }
  return w <= -Math.PI ? w + TAU : w;
}

/** The screen-frame point of a spot when the globe is centred on `centre`. */
export function toScreenPoint(spot: Spot, centre: Spot): ScreenPoint {
  const d = spot.lon - centre.lon;
  const cLat = Math.cos(spot.lat);
  const cd = Math.cos(d);
  return {
    x: cLat * Math.sin(d),
    y: Math.cos(centre.lat) * Math.sin(spot.lat) - Math.sin(centre.lat) * cLat * cd,
    z: Math.sin(centre.lat) * Math.sin(spot.lat) + Math.cos(centre.lat) * cLat * cd,
  };
}

/** The spot under a screen-frame point (inverse of toScreenPoint). */
export function fromScreenPoint(p: ScreenPoint, centre: Spot): Spot {
  const sinLat = p.z * Math.sin(centre.lat) + p.y * Math.cos(centre.lat);
  const lon = centre.lon + Math.atan2(p.x, p.z * Math.cos(centre.lat) - p.y * Math.sin(centre.lat));
  return { lon: wrapPi(lon), lat: Math.asin(Math.max(-1, Math.min(1, sinLat))) };
}

/**
 * Where the globe must be centred for `grab` to sit under screen point `p` (a drag: the grabbed
 * point follows the cursor). North stays up, so the centre has two unknowns and p gives two
 * equations. With Δ = grab.lon - lon0:
 *
 *   x = cos(lat) sin Δ                       → Δ (two roots: the one nearest `prev` wins)
 *   z + i·y = (cos(lat) cos Δ + i·sin(lat)) · e^(-i·lat0)   → lat0 by the angle between them
 *
 * Returns null only when no centre fits inside ±maxLat and the closest one cannot be clamped.
 */
export function centreFor(grab: Spot, p: ScreenPoint, prev: Spot, maxLat: number): Spot | null {
  const cg = Math.cos(grab.lat);
  const sg = Math.sin(grab.lat);
  const sinD = cg > POLE_EPS ? Math.max(-1, Math.min(1, p.x / cg)) : 0;
  const d1 = Math.asin(sinD);
  let best: Spot | null = null;
  let bestCost = Infinity;

  for (const d of [d1, Math.PI - d1]) {
    const lat0 = wrapPi(Math.atan2(sg, cg * Math.cos(d)) - Math.atan2(p.y, p.z));
    const clamped = Math.max(-maxLat, Math.min(maxLat, lat0));
    const lon0 = wrapPi(grab.lon - d);
    const cost = Math.abs(wrapPi(lon0 - prev.lon)) + Math.abs(lat0 - prev.lat) + Math.abs(lat0 - clamped) * TAU;
    if (cost < bestCost) {
      bestCost = cost;
      best = { lon: lon0, lat: clamped };
    }
  }
  return best;
}
